import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useApiContext } from "./ApiContext";

type Edition = "express" | "latexlab" | "converter" | "pro" | "institution";

interface LicenseState {
  edition: Edition;
  features: string[];
  maxSnapshots: number;
  isLoading: boolean;
}

interface LicenseContextValue extends LicenseState {
  hasFeature: (feature: string) => boolean;
  requiresUpgrade: (feature: string) => string | null;
  refetch: () => void;
}

const UPGRADE_MAP: Record<string, string> = {
  formulaCreate: "latexlab",
  formulaEditor: "latexlab",
  formulaLibrary: "latexlab",
  formulaCopy: "latexlab",
  cloudSync: "pro",
  collaboration: "pro",
  unlimitedSnapshots: "pro",
  // Using a table you already have inside a paper is a Tables feature, not an
  // editor one: inserting a table INTO a document is part of the document and
  // stays open, while reusing one FROM the tables section is what the Tables
  // tool sells. The tier name is a product decision — change it here.
  // Not checked anywhere for now: Use in paper is open until the gate returns.
  tableUseInDocument: "tables",
  importDocx: "converter",
  importPdf: "converter",
  exportDocx: "converter",
  exportHtml: "converter",
  exportMarkdown: "converter",
  exportTypst: "converter",
};

const LicenseContext = createContext<LicenseContextValue | null>(null);

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { fetchWithAuth, isSignedIn } = useApiContext();
  const [state, setState] = useState<LicenseState>({
    edition: "express",
    features: [],
    maxSnapshots: 3,
    isLoading: true,
  });

  const fetchLicense = useCallback(async () => {
    const isE2E = !!import.meta.env.VITE_E2E;
    if (!isSignedIn && !isE2E) {
      setState({
        edition: "express",
        features: [],
        maxSnapshots: 3,
        isLoading: false,
      });
      return;
    }

    try {
      const response = isE2E
        ? await fetch("/api/license/status")
        : await fetchWithAuth("/api/license/status");
      if (response.ok) {
        const data = await response.json();
        setState({
          edition: data.edition,
          features: data.features,
          maxSnapshots: data.maxSnapshots,
          isLoading: false,
        });
      } else {
        // License service unavailable (e.g. Next.js not running in dev) — use defaults silently
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } catch {
      // Network error — license service unreachable, use defaults silently
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [fetchWithAuth, isSignedIn]);

  // Fetch on mount
  useEffect(() => {
    fetchLicense();
  }, [fetchLicense]);

  // Re-fetch on window focus (in case user upgraded in another tab)
  useEffect(() => {
    function handleFocus() {
      fetchLicense();
    }
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchLicense]);

  const hasFeature = useCallback(
    (feature: string) => state.features.includes(feature),
    [state.features],
  );

  const requiresUpgrade = useCallback(
    (feature: string): string | null => {
      if (state.features.includes(feature)) return null;
      return UPGRADE_MAP[feature] ?? "pro";
    },
    [state.features],
  );

  const value = useMemo(
    () => ({
      ...state,
      hasFeature,
      requiresUpgrade,
      refetch: fetchLicense,
    }),
    [state, hasFeature, requiresUpgrade, fetchLicense],
  );

  return (
    <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicense must be used within a LicenseProvider");
  }
  return context;
}
